import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useMatch, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, type Me, type MeResponse, type ProjectSummary } from './api';
import { loadExtra } from './i18n';
import { CommandPaletteProvider } from './commands';
import { AppTopbar } from './components/AppTopbar';
import { LoginPage } from './pages/LoginPage';
import { PendingPage } from './pages/PendingPage';
import { ProjectLayout } from './pages/ProjectLayout';
import { useBrand, useSkin, useThemeRuntime } from './theme';
import { useCustomisation } from './customise';
import { nativeBridge } from './native';

/*
 * §17 perf: only the shell (login, project tree, folder views) is in the first download. Every other page is its own
 * chunk, loaded on first visit — so the heavy libraries load only where they're used: the Glide grid (sheets, plans),
 * ProseMirror + Yjs + diff (documents), charts (dashboards, reports).
 */
const page = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  // §23 M12.6 with the strings lazy pages use (fetched once, alongside the first one)
  lazy(() => Promise.all([load(), loadExtra()]).then(([m]) => ({ default: m[name] })));
/** #153 the new-project dialog (with its programme picker) loads when it's opened, not with the shell. */
export const NewProjectDialog = lazy(() =>
  import('./components/NewProjectDialog').then((m) => ({ default: m.NewProjectDialog })),
);
/** Data lifecycle: archive / restore / retention (organisation admins), with its strings, when it's opened. */
export const ArchiveDialog = lazy(() =>
  Promise.all([import('./components/ArchiveDialog'), loadExtra()]).then(([m]) => ({
    default: m.ArchiveDialog,
  })),
);
/** …and the read-only banner, only on an archived project. */
export const ArchiveBanner = lazy(() =>
  Promise.all([import('./components/ArchiveBanner'), loadExtra()]).then(([m]) => ({
    default: m.ArchiveBanner,
  })),
);
/**
 * A folder's page (and the command bar, its icons and specs that came with it on 1 Nov) loads with the first project
 * opened, not with the sign-in screen. Its strings are still in the core file, so no extra strings fetch.
 */
const FolderPage = lazy(() => import('./pages/FolderPage').then((m) => ({ default: m.FolderPage })));
/** 3 Nov review: a My Workspace page beside the tree — with that page's own chunk, not the first download. */
const WorkspaceShell = lazy(() =>
  import('./pages/WorkspaceShell').then((m) => ({ default: m.WorkspaceShell })),
);
const ProfilePage = page(() => import('./pages/ProfilePage'), 'ProfilePage');
const ConnectedPage = page(() => import('./pages/ConnectedPage'), 'ConnectedPage');
const MailPage = page(() => import('./pages/MailPage'), 'MailPage');
const SharePage = page(() => import('./pages/SharePage'), 'SharePage');
const BrowserPage = page(() => import('./pages/BrowserPage'), 'BrowserPage');
const PortfolioPage = page(() => import('./pages/PortfolioPage'), 'PortfolioPage');
const PeoplePage = page(() => import('./pages/PeoplePage'), 'PeoplePage');
const SharedPage = page(() => import('./pages/SharedPage'), 'SharedPage');
const ProgrammePage = page(() => import('./pages/PortfolioPage'), 'ProgrammePage');
const ConnectedFoldersPage = page(() => import('./pages/ConnectedPage'), 'ConnectedFoldersPage');
const AdministrationPage = page(() => import('./pages/AdministrationPage'), 'AdministrationPage');
const BoardPage = page(() => import('./pages/BoardPage'), 'BoardPage');
const DashboardPage = page(() => import('./pages/DashboardPage'), 'DashboardPage');
const ExecDashboardPage = page(() => import('./pages/DashboardPage'), 'ExecDashboardPage');
const HomeDashboardPage = page(() => import('./pages/HomeDashboardPage'), 'HomeDashboardPage');
const DocumentPage = page(() => import('./pages/DocumentPage'), 'DocumentPage');
const GridPage = page(() => import('./pages/GridPage'), 'GridPage');
const PlanPage = page(() => import('./pages/PlanPage'), 'PlanPage');
const ReportPage = page(() => import('./pages/ReportPage'), 'ReportPage');
const WorkflowsPage = page(() => import('./pages/WorkflowsPage'), 'WorkflowsPage');
const ActionsPage = page(() => import('./pages/ActionsPage'), 'ActionsPage');
const BrandSettingsPage = page(() => import('./pages/BrandSettingsPage'), 'BrandSettingsPage');
const ExternalReviewPage = page(() => import('./pages/ExternalReviewPage'), 'ExternalReviewPage');
// §37 the desktop app's download page (public)
const DownloadPage = page(() => import('./pages/DownloadPage'), 'DownloadPage');
const ChatPage = page(() => import('./pages/ChatPage'), 'ChatPage');
const ChatRedirect = page(() => import('./pages/ChatPage'), 'ChatRedirect');
const CallPage = page(() => import('./pages/CallPage'), 'CallPage');
const PlatformPage = page(() => import('./pages/PlatformPage'), 'PlatformPage');
const LearningLayout = page(() => import('./pages/LearningPage'), 'LearningLayout');
const LearningHome = page(() => import('./pages/LearningPage'), 'LearningHome');
const CoursePlayerPage = page(() => import('./pages/CoursePlayerPage'), 'CoursePlayerPage');
const CourseEditorPage = page(() => import('./pages/CourseEditorPage'), 'CourseEditorPage');

/** Placeholder while a page's chunk loads (usually a few ms after the first visit; then it's cached). */
function Loading({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="page-loading" aria-busy="true" />}>{children}</Suspense>;
}

export function useMeResponse() {
  return useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/auth/me'), retry: false });
}

/** The signed-in person with an ACTIVE context (only used under <RequireAuth>, which sends pending people away). */
export function useMe() {
  const q = useMeResponse();
  return { ...q, data: q.data && !q.data.pending ? (q.data as Me) : undefined };
}

/** §23 M12.2 may the signed-in account download / export here? (The api enforces it; this hides the buttons.) */
export function useExportsAllowed() {
  return useMe().data?.exportsAllowed ?? true;
}

/** The Browser beside the tree (which folds away), except popped out into a window of its own. */
function BrowserRoute() {
  const [params] = useSearchParams();
  const page = (
    <Loading>
      <BrowserPage />
    </Loading>
  );
  return params.get('window') === '1' ? (
    page
  ) : (
    <Loading>
      <WorkspaceShell selected="browser" collapsible>
        {page}
      </WorkspaceShell>
    </Loading>
  );
}

function Home() {
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectSummary[]>('/projects'),
  });
  if (projects.isLoading) return <div className="center-msg">…</div>;
  const first = projects.data?.[0];
  if (!first) return <NoProjects />;
  return <Navigate to={`/p/${first.id}`} replace />;
}

/**
 * First run / not on any project yet: the normal top bar (so Admin, Platform and the avatar menu are reachable) and
 * an empty state — tenant admins (the only people who can create projects) get "Create your first project"; everyone
 * else is told an administrator will add them, with Learning still to hand.
 */
function NoProjects() {
  const { t } = useTranslation();
  const me = useMe().data!;
  const [creating, setCreating] = useState(false);
  return (
    <div className="shell home-shell">
      <AppTopbar me={me} />
      <main className="home-empty">
        <div className="empty-card">
          <img src="/favicon.svg" alt="" width={44} height={44} />
          {me.isTenantAdmin ? (
            <>
              <h1>{t('home.adminTitle', { org: me.tenant.name })}</h1>
              <p className="muted">{t('home.adminText')}</p>
              <button className="btn btn-primary btn-lg" onClick={() => setCreating(true)}>
                + {t('home.createFirst')}
              </button>
            </>
          ) : (
            <>
              <h1>{t('home.allSet')}</h1>
              <p className="muted">{t('home.waitText')}</p>
            </>
          )}
          {me.features.learning && (
            <p className="small">
              <Link to="/learning">{t('home.learning')}</Link>
            </p>
          )}
        </div>
      </main>
      {creating && (
        <Suspense fallback={null}>
          <NewProjectDialog onClose={() => setCreating(false)} />
        </Suspense>
      )}
    </div>
  );
}

/** Remount per course, so a player's local state never carries over to another course. */
function CoursePlayerRoute() {
  const { courseId } = useParams();
  return <CoursePlayerPage key={courseId} />;
}

/** Remount per sheet so selection, column widths and realtime room never leak between sheets. */
function GridRoute() {
  const { gridId } = useParams();
  return <GridPage key={gridId} />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const me = useMeResponse();
  if (me.isLoading) return <div className="center-msg">…</div>;
  if (me.isError || !me.data) return <Navigate to="/login" replace />;
  if (me.data.pending) return <Navigate to="/pending" replace />;
  return <>{children}</>;
}

/** §17 applies the light/dark choice, the skin and the active tenant's (and project's) brand, app-wide. */
function ThemeRuntime() {
  useThemeRuntime();
  // Not on the sign-in page (nobody is signed in there; it shows the base or the ?org= brand itself), nor on an
  // external review page (§18: no account — and their own Fyr session, if any, plays no part).
  const login = useMatch('/login');
  const review = useMatch('/review');
  const onLogin = !!login || !!review;
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    retry: false,
    enabled: !onLogin,
  });
  const tenantId = !onLogin && me.data && !me.data.pending ? (me.data as Me).tenant.id : undefined;
  useBrand(tenantId);
  // §23 M12.6 the skin: the organisation's once signed in, the platform default before (incl. sign-in, reviews)
  useSkin(tenantId);
  // Customisation console: text overrides (platform + organisation) and a super admin's click-to-edit
  useCustomisation(tenantId ? (me.data as Me).tenant : undefined);
  return null;
}

export function App() {
  return (
    <>
      <ThemeRuntime />
      <PaletteHost>
        <AppRoutes />
      </PaletteHost>
    </>
  );
}

/** Cmd/Ctrl+K (command bar spec §10) for anyone signed in to an organisation — not on sign-in, reviews or the console. */
function PaletteHost({ children }: { children: ReactNode }) {
  // every useMatch on every render (a short-circuiting || skipped hooks and broke the hook order after sign-in)
  const matches = [useMatch('/login'), useMatch('/review'), useMatch('/pending'), useMatch('/platform')];
  const off = matches.some(Boolean);
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    retry: false,
    enabled: !off,
  });
  // #155.2 in the app, once signed in: push notifications for this account (loaded only inside the app)
  const meId = me.data && !me.data.pending ? me.data.id : null;
  useEffect(() => {
    if (meId && nativeBridge())
      void import('./native-push').then((m) => m.signedIn({ id: meId })).catch(() => undefined);
  }, [meId]);
  return (
    <CommandPaletteProvider enabled={!off && !!me.data && !me.data.pending}>
      {children}
    </CommandPaletteProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/pending" element={<PendingPage />} />
      <Route
        path="/download"
        element={
          <Loading>
            <DownloadPage />
          </Loading>
        }
      />
      <Route
        path="/review"
        element={
          <Loading>
            <ExternalReviewPage />
          </Loading>
        }
      />
      <Route
        path="/profile"
        element={
          <Loading>
            <ProfilePage />
          </Loading>
        }
      />
      <Route
        path="/shared"
        element={
          <RequireAuth>
            <Loading>
              <SharedPage />
            </Loading>
          </RequireAuth>
        }
      />
      {/* #142 M19 the people directory and colleague connections */}
      <Route
        path="/people"
        element={
          <RequireAuth>
            <Loading>
              <PeoplePage />
            </Loading>
          </RequireAuth>
        }
      />
      {/* #155.3 Share to Fyr: what another app shared, into a folder or a new action (the Fyr app) */}
      <Route
        path="/share"
        element={
          <RequireAuth>
            <Loading>
              <SharePage />
            </Loading>
          </RequireAuth>
        }
      />
      {/* §34 M24 the person's own connected mailbox (not a project's) */}
      <Route
        path="/mail"
        element={
          <RequireAuth>
            <Loading>
              <WorkspaceShell selected="mail">
                <Loading>
                  <MailPage />
                </Loading>
              </WorkspaceShell>
            </Loading>
          </RequireAuth>
        }
      />
      {/* §36 M25 the Fyr Browser (desktop app; the web explains how to get it) */}
      <Route
        path="/browser"
        element={
          <RequireAuth>
            <BrowserRoute />
          </RequireAuth>
        }
      />
      {/* the personal Dashboard: my activities, milestones and calendar across all my projects */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <Loading>
              <WorkspaceShell selected="myDashboard">
                <Loading>
                  <HomeDashboardPage />
                </Loading>
              </WorkspaceShell>
            </Loading>
          </RequireAuth>
        }
      />
      {/* #143 the organisation's programmes and projects; a programme's page */}
      <Route
        path="/portfolio"
        element={
          <RequireAuth>
            <Loading>
              <PortfolioPage />
            </Loading>
          </RequireAuth>
        }
      />
      <Route
        path="/programme/:programmeId"
        element={
          <RequireAuth>
            <Loading>
              <ProgrammePage />
            </Loading>
          </RequireAuth>
        }
      />
      {/* §M18 the folders this person has connected (and those on their account only) */}
      {['/connected', '/connected/:sourceId'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <RequireAuth>
              <Loading>
                <ConnectedFoldersPage />
              </Loading>
            </RequireAuth>
          }
        />
      ))}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Home />
          </RequireAuth>
        }
      />
      <Route
        path="/p/:projectId"
        element={
          <RequireAuth>
            <ProjectLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="f/root" replace />} />
        <Route path="g/:gridId" element={<GridRoute />} />
        <Route path="f/:folderId" element={<FolderPage />} />
        <Route path="cf/:sourceId" element={<ConnectedPage />} />
        <Route path="workspace" element={<FolderPage workspace />} />
        <Route path="d/:docId" element={<DocumentPage />} />
        <Route path="plan/:planId" element={<PlanPage />} />
        <Route path="board/:boardId" element={<BoardPage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="actions" element={<ActionsPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="dashboard/exec" element={<ExecDashboardPage />} />
        <Route path="report" element={<ReportPage />} />
        <Route path="report/:reportId" element={<ReportPage />} />
        {/* §23 M12.4 the old per-project admin screen is now the tenant-wide Administration area */}
        <Route path="admin" element={<Navigate to="/admin" replace />} />
        <Route path="settings/brand" element={<BrandSettingsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:channelId" element={<ChatPage />} />
        <Route path="call/:callId" element={<CallPage />} />
      </Route>
      {/* §23 M12.4 Administration: tenant-wide, its own shell (it shows each person only their sections). */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <Loading>
              <AdministrationPage />
            </Loading>
          </RequireAuth>
        }
      />
      {/* §23 M12.3 the Ibexstone platform console: its own route and chrome; platform admins only (it checks). */}
      <Route
        path="/platform"
        element={
          <Loading>
            <PlatformPage />
          </Loading>
        }
      />
      {/* §21 Learning is tenant-wide: its own shell, reachable without being on any project. */}
      <Route
        path="/learning"
        element={
          <RequireAuth>
            <Loading>
              <LearningLayout />
            </Loading>
          </RequireAuth>
        }
      >
        <Route index element={<LearningHome />} />
        <Route path="c/:courseId" element={<CoursePlayerRoute />} />
        <Route path="c/:courseId/edit" element={<CourseEditorPage />} />
      </Route>
      {/* §19 links in notifications / emails: a conversation, opened inside one of your projects */}
      <Route
        path="/chat/:channelId"
        element={
          <RequireAuth>
            <Loading>
              <ChatRedirect />
            </Loading>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
